import React from "react";
import WorkCodeField from "../../../../shared/WorkCodeField/WorkCodeField";
import { swatchHex } from "../../../../../utils/color";
import { groupUntaggedActivities } from "../../../../../api/clientV2";
import type { ExistingWorkCode, SuggestedCode, UntaggedGroup } from "../../../../../models";
import { buildCommitted } from "../../tree";
import type { SectionMeta, TreeActivity } from "../../tree";
import type { ActivityMove, CodeEdit } from "../../state";
import "./UntaggedGroups.css";

interface Props {
  scheduleId: number;
  /** The section whose untagged rail this is — the move target. */
  sectionName: string;
  /** This rail's untagged activities — drives the grouping fetch + the
   *  committed snapshots a move needs. */
  untagged: TreeActivity[];
  sessionCodes?: ExistingWorkCode[];
  /** Every section name — for the per-row Section dropdown (move cross-section). */
  sectionOptions: string[];
  getSectionMeta: (section: string) => SectionMeta;
  /** Picking a different section lazy-loads its A/B/C. */
  onNeedSection: (section: string) => void;
  /** Stage the ready activities onto the board — one undo step in the host. */
  onMoveToBoard: (moves: ActivityMove[]) => void;
  colors: Record<string, string>;
  codeEdits: Record<string, CodeEdit>;
  /** The draft's re-zoned/moved activities (effective placement) so the WBS-leaf
   *  vote follows the user's in-progress fixes, not the stale committed tags. */
  stagedZones?: StagedZone[];
  /** Activities the draft untagged — drop their stale committed zone from the vote. */
  excludedIds?: number[];
  /** Code-only mode: the activities already have their section/zone/level (from
   *  the WBS-node mapping), so DON'T show or set those — just assign a work code
   *  per group. A row is ready the instant its group has a code. The move sets
   *  only the code; the board resolves the zone from the node. */
  codeOnly?: boolean;
}

export interface StagedZone {
  activity_id: number;
  section_name?: string;
  takt_a?: string;
  takt_b?: string;
  takt_c?: string;
}

interface ActDraft {
  section: string;
  takt_a: string;
  takt_b: string;
  takt_c: string;
}
interface GroupDraft {
  code: string;
  name: string;
  color: string;
  acts: Record<number, ActDraft>;
}

const dropOptions = (opts: string[], current: string): string[] => {
  if (!current || opts.indexOf(current) !== -1) return opts;
  return [current, ...opts];
};

// `|` is the separator in the structure tree's zone/level keys; strip it from a
// free-typed zone/level so a typed pipe can't forge a colliding key.
const noPipe = (s: string): string => s.replace(/\|/g, "");

/** "matched" (reused an existing code) or "new" (auto-created from the prefix),
 *  but only while the draft still holds the suggested code — once the user picks
 *  or types something else, the badge clears. */
const groupBadge = (
  sug: SuggestedCode | null,
  gd: GroupDraft,
): "matched" | "new" | null => {
  if (!sug || gd.code !== sug.code || gd.name !== sug.prefix) return null;
  return sug.is_new ? "new" : "matched";
};

/** "Group untagged": clusters this section's untagged activities by fuzzy name
 *  (backend), pre-fills section/zone/level from the WBS-leaf vote, and lets the
 *  user set one work code per group + tweak placement, then "Move to board"
 *  stages the ready rows. No automatic moving. */
const UntaggedGroups: React.FC<Props> = ({
  scheduleId,
  sectionName,
  untagged,
  sessionCodes,
  sectionOptions,
  getSectionMeta,
  onNeedSection,
  onMoveToBoard,
  colors,
  codeEdits,
  stagedZones,
  excludedIds,
  codeOnly,
}) => {
  const [groups, setGroups] = React.useState<UntaggedGroup[] | null>(null);
  const [draft, setDraft] = React.useState<GroupDraft[]>([]);
  const [moved, setMoved] = React.useState<Set<number>>(new Set());

  const byId = React.useMemo(() => {
    const m: Record<number, TreeActivity> = {};
    untagged.forEach((a) => {
      m[a.id] = a;
    });
    return m;
  }, [untagged]);

  // getSectionMeta scans the whole board on every call; a rail's rows nearly all
  // share one section, so cache per distinct section. The cache rebuilds only when
  // getSectionMeta's identity changes (i.e. the underlying board data changed) —
  // not on every keystroke that just edits a group's draft.
  const sectionMetaFor = React.useMemo(() => {
    const cache = new Map<string, SectionMeta>();
    return (section: string): SectionMeta => {
      let m = cache.get(section);
      if (!m) {
        m = getSectionMeta(section);
        cache.set(section, m);
      }
      return m;
    };
  }, [getSectionMeta]);

  React.useEffect(() => {
    let cancelled = false;
    setGroups(null);
    groupUntaggedActivities(
      scheduleId,
      untagged.map((a) => a.id),
      stagedZones,
      excludedIds,
    ).then((res) => {
      if (cancelled) return;
      // No-name-match activities become singleton groups so the UX is uniform.
      const all: UntaggedGroup[] = res.groups.concat(
        res.no_match.map((a) => ({
          name: a.task_name || "Untitled",
          suggested: null,
          activities: [a],
        })),
      );
      setGroups(all);
      setDraft(
        all.map((g) => {
          const acts: Record<number, ActDraft> = {};
          g.activities.forEach((a) => {
            // Default to THIS rail's section (the loaded one whose zones/levels
            // the dropdowns scope to). The vote's section is the rail's anyway
            // for untagged-in-leaf rows; the user can still move cross-section.
            acts[a.id] = {
              section: sectionName,
              takt_a: a.takt_a,
              takt_b: a.takt_b,
              takt_c: a.takt_c,
            };
          });
          // Auto-fill the code only when the backend matched an existing one.
          return {
            code: g.suggested?.code || "",
            name: g.suggested?.prefix || "",
            color: g.suggested?.color || "",
            acts,
          };
        }),
      );
    });
    return () => {
      cancelled = true;
    };
    // Fetch once when the rail enters grouped mode; ids are captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const setGroupCode = (gi: number, p: { code?: string; name?: string; color?: string }) =>
    setDraft((prev) =>
      prev.map((g, i) =>
        i === gi
          ? {
              ...g,
              code: p.code ?? g.code,
              name: p.name ?? g.name,
              color: p.color ?? g.color,
            }
          : g,
      ),
    );

  const setActZone = (gi: number, id: number, p: Partial<ActDraft>) =>
    setDraft((prev) =>
      prev.map((g, i) =>
        i === gi ? { ...g, acts: { ...g.acts, [id]: { ...g.acts[id], ...p } } } : g,
      ),
    );

  // Changing an activity's section rescopes phase/zone/level to that section.
  const setActSection = (gi: number, id: number, section: string) => {
    const meta = sectionMetaFor(section);
    setActZone(gi, id, { section, takt_a: meta.taktAs[0] || "", takt_b: "", takt_c: "" });
    if (section) onNeedSection(section);
  };

  // A row is ready when its group has a code AND it has a section + zone — except
  // in code-only mode, where section/zone come from the node and a code is enough.
  const rowReady = (gd: GroupDraft | undefined, id: number): boolean => {
    if (!gd || !gd.code.trim()) return false;
    if (codeOnly) return true;
    const e = gd.acts[id];
    return Boolean(e && e.section.trim() && e.takt_b.trim());
  };

  // Build the moves for a group's READY rows — pure, doesn't apply.
  const collectGroupMoves = (
    gi: number,
    g: UntaggedGroup,
  ): { moves: ActivityMove[]; ids: number[] } => {
    const gd = draft[gi];
    const moves: ActivityMove[] = [];
    const ids: number[] = [];
    if (!gd || !gd.code.trim()) return { moves, ids };
    g.activities.forEach((a) => {
      if (moved.has(a.id) || !rowReady(gd, a.id)) return;
      const act = byId[a.id];
      const e = gd.acts[a.id];
      if (!act) return;
      // Save the hue the picker swatch shows — the chosen color, or the code's
      // derived hue when the picker was never opened (else a blank chip).
      const codeFields = {
        code: gd.code.trim(),
        name: gd.name,
        color: swatchHex(gd.color, gd.code.trim()),
      };
      // Code-only: leave section/zone alone — they come from the node and the
      // board resolves them. Otherwise place the activity (section + zone) too.
      const patch = codeOnly
        ? codeFields
        : {
            ...codeFields,
            section: e.section,
            takt_a: e.takt_a || getSectionMeta(e.section).taktAs[0] || "",
            takt_b: e.takt_b,
            takt_c: e.takt_c,
          };
      moves.push({
        id: a.id,
        patch,
        committed: buildCommitted(act, codeEdits, colors),
      });
      ids.push(a.id);
    });
    return { moves, ids };
  };

  const applyMoves = (moves: ActivityMove[], ids: number[]) => {
    if (!moves.length) return;
    onMoveToBoard(moves);
    setMoved((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => n.add(id));
      return n;
    });
  };

  const moveGroup = (gi: number, g: UntaggedGroup) => {
    const { moves, ids } = collectGroupMoves(gi, g);
    applyMoves(moves, ids);
  };

  // A group is "fully ready" when it has a code AND every remaining row is ready.
  const groupFullyReady = (gi: number, g: UntaggedGroup): boolean => {
    const gd = draft[gi];
    if (!gd || !gd.code.trim()) return false;
    const rows = g.activities.filter((a) => !moved.has(a.id));
    return rows.length > 0 && rows.every((a) => rowReady(gd, a.id));
  };

  const moveAllReady = () => {
    if (!groups) return;
    const moves: ActivityMove[] = [];
    const ids: number[] = [];
    groups.forEach((g, gi) => {
      if (!groupFullyReady(gi, g)) return;
      const r = collectGroupMoves(gi, g);
      r.moves.forEach((m) => moves.push(m));
      r.ids.forEach((id) => ids.push(id));
    });
    applyMoves(moves, ids);
  };

  if (groups === null) return <div className="takt-editor-hint">Grouping…</div>;

  const readyGroupCount = groups.filter((g, gi) => groupFullyReady(gi, g)).length;
  const visibleGroupCount = groups.filter(
    (g) => g.activities.some((a) => !moved.has(a.id)),
  ).length;

  return (
    <div className="takt-editor-groups">
      {visibleGroupCount > 0 && (
        <div className="takt-editor-groups-bar">
          <span className="takt-editor-groups-bar-count">
            {readyGroupCount} of {visibleGroupCount} ready
          </span>
          <button
            type="button"
            className="takt-editor-groups-bar-btn"
            disabled={readyGroupCount === 0}
            onClick={moveAllReady}
            title="Move every group whose rows all have a code, section & zone to the board"
          >
            Move {readyGroupCount} ready {readyGroupCount === 1 ? "group" : "groups"} to board
          </button>
        </div>
      )}
      {groups.map((g, gi) => {
        const rows = g.activities.filter((a) => !moved.has(a.id));
        const gd = draft[gi];
        if (rows.length === 0 || !gd) return null;
        const readyCount = rows.filter((a) => rowReady(gd, a.id)).length;
        return (
          <div key={gi} className="takt-editor-group">
            <div className="takt-editor-group-head">
              <div className="takt-editor-group-head-top">
                <span className="takt-editor-group-name" title={g.name}>
                  {g.name}
                </span>
                <span className="takt-editor-group-count">×{rows.length}</span>
              </div>
              <div className="takt-editor-group-code">
                <span className="takt-editor-group-code-label">Work code</span>
                <WorkCodeField
                  scheduleId={scheduleId}
                  code={gd.code}
                  name={gd.name}
                  color={gd.color}
                  onChange={(p) => setGroupCode(gi, p)}
                  extraCodes={sessionCodes}
                  badge={groupBadge(g.suggested, gd)}
                  defaultNew={groupBadge(g.suggested, gd) === "new"}
                />
              </div>
            </div>

            <div className="takt-editor-group-rows">
              {rows.map((a) => {
                if (codeOnly) {
                  // Section/zone/level are already set by the node — show just the
                  // activity name; the group's code is all that's needed.
                  return (
                    <div
                      key={a.id}
                      className={`takt-editor-group-row is-codeonly ${a.confident ? "" : "is-unsure"}`}
                    >
                      <span className="takt-editor-group-row-name" title={a.task_name}>
                        {a.task_name}
                      </span>
                    </div>
                  );
                }
                const e = gd.acts[a.id];
                const rmeta = sectionMetaFor(e.section);
                const phaseOpts = dropOptions(rmeta.taktAs, e.takt_a);
                const zoneOpts = dropOptions(rmeta.zones, e.takt_b);
                const levelOpts = dropOptions(rmeta.levelsByZone[e.takt_b] ?? [], e.takt_c);
                return (
                  <div
                    key={a.id}
                    className={`takt-editor-group-row ${a.confident ? "" : "is-unsure"}`}
                  >
                    <span className="takt-editor-group-row-name" title={a.task_name}>
                      {a.task_name}
                    </span>
                    <select
                      className="takt-editor-group-sel takt-editor-group-sel--section"
                      value={e.section}
                      onChange={(ev) => setActSection(gi, a.id, ev.target.value)}
                      aria-label="Section"
                      title={e.section}
                    >
                      <option value="">Section…</option>
                      {dropOptions(sectionOptions, e.section).map((s) => (
                        <option key={s} value={s}>
                          {s || "—"}
                        </option>
                      ))}
                    </select>
                    <input
                      className="takt-editor-group-sel takt-editor-group-combo"
                      list={`te-phase-${gi}-${a.id}`}
                      value={e.takt_a}
                      placeholder="Phase…"
                      onChange={(ev) => setActZone(gi, a.id, { takt_a: ev.target.value })}
                      aria-label="Phase"
                      title={e.takt_a}
                    />
                    <datalist id={`te-phase-${gi}-${a.id}`}>
                      {phaseOpts.map((ph) => (
                        <option key={ph} value={ph} />
                      ))}
                    </datalist>
                    <input
                      className="takt-editor-group-sel takt-editor-group-combo"
                      list={`te-zone-${gi}-${a.id}`}
                      value={e.takt_b}
                      placeholder="Zone…"
                      onChange={(ev) => setActZone(gi, a.id, { takt_b: noPipe(ev.target.value) })}
                      aria-label="Zone"
                      title={e.takt_b}
                    />
                    <datalist id={`te-zone-${gi}-${a.id}`}>
                      {zoneOpts.map((z) => (
                        <option key={z} value={z} />
                      ))}
                    </datalist>
                    <input
                      className="takt-editor-group-sel takt-editor-group-combo"
                      list={`te-level-${gi}-${a.id}`}
                      value={e.takt_c}
                      placeholder="Level…"
                      onChange={(ev) => setActZone(gi, a.id, { takt_c: noPipe(ev.target.value) })}
                      aria-label="Level"
                      title={e.takt_c}
                    />
                    <datalist id={`te-level-${gi}-${a.id}`}>
                      {levelOpts.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="takt-editor-group-move"
              disabled={readyCount === 0}
              onClick={() => moveGroup(gi, g)}
              title={
                !gd.code.trim()
                  ? "Set a work code for this group first"
                  : readyCount === 0
                    ? "Pick a zone for at least one activity"
                    : "Move the ready activities onto the board"
              }
            >
              Move {readyCount} to board
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default UntaggedGroups;
